<template>
  <Dialog
    v-model:visible="visible"
    :modal="true"
    :closable="true"
    :draggable="false"
    :style="{ width: '560px' }"
    :pt="{ root: { style: 'max-height: 90vh; overflow-y: auto;' }, header: { style: 'border-bottom: 1px solid var(--p-surface-700); margin-bottom: 1rem;' } }"
    @hide="$emit('close')"
  >
    <!-- Header -->
    <template #header>
      <div class="dialog-header-title">
        <span class="material-icons dialog-header-icon">add_circle</span>
        <div>
          <h3>{{ $t('botEditor.tools.addToolModal.selectTitle') }}</h3>
          <p>{{ $t('botEditor.tools.addToolModal.selectSubtitle') }}</p>
        </div>
      </div>
    </template>

    <div class="dialog-body">
      <div class="tools-grid">

        <!-- Crea tool flow -->
        <div id="btn-create-tool-flow" class="tool-card tool-card--custom" @click="$emit('create-tool-flow')">
          <div class="tool-icon-wrap" style="background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)">
            <span class="material-icons">build_circle</span>
          </div>
          <div class="tool-card-info">
            <span class="tool-card-name">{{ $t('botEditor.tools.addToolModal.createToolName') }}</span>
            <span class="tool-card-sub">{{ $t('botEditor.tools.addToolModal.createToolSub') }}</span>
          </div>
          <span class="material-icons tool-card-arrow">chevron_right</span>
        </div>

        <!-- MCP HTTP -->
        <div id="btn-add-http-mcp" class="tool-card tool-card--http" @click="$emit('open-http-modal')">
          <div class="tool-icon-wrap" style="background: linear-gradient(135deg, #374151 0%, #1f2937 100%); border: 1px solid #4b5563">
            <span class="material-icons">power</span>
          </div>
          <div class="tool-card-info">
            <span class="tool-card-name">{{ $t('botEditor.tools.addToolModal.httpServerName') }}</span>
            <span class="tool-card-sub">{{ $t('botEditor.tools.addToolModal.httpServerSub') }}</span>
          </div>
          <span class="material-icons tool-card-arrow">chevron_right</span>
        </div>

      </div>
    </div>
  </Dialog>
</template>

<script setup>
/**
 * AddToolModal.vue
 *
 * Modal for adding a tool to a bot: create a custom tool flow, or connect
 * an external HTTP MCP server.
 *
 * @example
 * <AddToolModal :botId="bot._id" @close="showAddModal = false" @open-http-modal="openHttpModal" @create-tool-flow="createToolFlow" />
 */
import { ref } from 'vue';

const props = defineProps({
  /** The ID of the bot this tool will be added to. */
  botId: { type: String, required: true },
});

defineEmits(['close', 'open-http-modal', 'create-tool-flow']);

/** Controls Dialog visibility; initialised to true so the modal opens immediately. */
const visible = ref(true);
</script>

<style scoped>
/* ── Header ── */
.dialog-header-title {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.dialog-header-icon {
  font-size: 22px;
  color: #818cf8;
  margin-top: 2px;
  flex-shrink: 0;
}

.dialog-header-title h3 {
  font-size: 16px;
  font-weight: 600;
  color: #f9fafb;
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.dialog-header-title p {
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
  line-height: 1.4;
}

.dialog-header-title strong {
  color: #e5e7eb;
  font-weight: 500;
}

/* ── Body ── */
.dialog-body {
  padding: 4px 0 4px;
}

/* ── Tools grid ── */
.tools-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ── Tool card ── */
.tool-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: #374151;
  border: 2px solid #4b5563;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.tool-card:hover {
  border-color: #6b7280;
  background: #3f4a5c;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}


.tool-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tool-icon-wrap .material-icons {
  font-size: 24px;
  color: white;
}

.tool-card-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.tool-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #f3f4f6;
  line-height: 1.2;
}

.tool-card-sub {
  font-size: 12px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 1.3;
}

.tool-card-arrow {
  font-size: 18px;
  color: #4b5563;
  flex-shrink: 0;
  transition: color 0.15s, transform 0.15s;
}

.tool-card:hover .tool-card-arrow {
  color: #9ca3af;
  transform: translateX(2px);
}
</style>
